package com.mrpowergamerbr.puppydough.webtale

import com.mrpowergamerbr.puppydough.DownloadUtils
import com.mrpowergamerbr.puppydough.MD5
import com.mrpowergamerbr.puppydough.VirtualGamepad
import com.mrpowergamerbr.puppydough.VirtualInputSink
import com.mrpowergamerbr.puppydough.OPFSUtils
import com.mrpowergamerbr.puppydough.WADUtils
import com.mrpowergamerbr.puppydough.clickToStart
import com.mrpowergamerbr.puppydough.el
import com.mrpowergamerbr.puppydough.plausible
import com.mrpowergamerbr.puppydough.processingGame
import com.mrpowergamerbr.puppydough.webtale.UIState.*
import js.buffer.toByteArray
import js.iterable.iterator
import js.promise.await
import jszip.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import web.blob.arrayBuffer
import web.blob.byteArray
import web.cssom.ClassName
import web.dom.*
import web.events.addEventHandler
import web.events.addHandler
import web.file.File
import web.file.FileList
import web.fs.*
import web.html.*
import web.keyboard.KEY_DOWN
import web.keyboard.KEY_UP
import web.keyboard.KeyboardEvent
import web.location.location
import web.navigator.navigator
import web.prompts.alert
import web.prompts.confirm
import web.storage.getDirectory
import web.storage.persist
import web.storage.sessionStorage
import web.streams.close
import web.url.URLSearchParams
import web.window.window
import kotlin.uuid.Uuid

fun ParentNode.compatibilityIcon(compatibility: GameCompatibility) = el(HtmlTagName.span) {
    className = ClassName("compatibility-icon ${compatibility.name.lowercase()}")
    title = when (compatibility) {
        GameCompatibility.COMPATIBLE -> "Compatible"
        GameCompatibility.UNKNOWN -> "Unknown version, it may not work correctly"
        GameCompatibility.INCOMPATIBLE -> "Incompatible version"
    }
}

var state: UIState = UploadGame
    set(value) { field = value; renderFromScratch() }   // every state change re-renders

val container
    get() = document.getElementById(ElementId("container"))!!

var config = WebtaleConfig(listOf(), null)
    private set

fun renderFromScratch() {
    container.replaceChildren()
    container.render()
}

suspend fun detectGame(inputFiles: FileList) {
    val files = mutableListOf<File>()

    for (file in inputFiles) {
        files.add(file)
    }

    val gameWADFile = files.firstOrNull { it.webkitRelativePath.substringAfter("/") in WADUtils.WAD_FILENAMES }
    if (gameWADFile == null) {
        alert("Couldn't find a Game WAD inside the game folder! Please make sure the game is in the root folder and has a data.win file inside it.")
        return
    }

    val dataWinArrayBuffer = gameWADFile.arrayBuffer()
    val dataWinBytes = dataWinArrayBuffer.toByteArray()
    val hash = MD5.hashHex(dataWinBytes)

    val gw = try {
        parseGameWAD(dataWinArrayBuffer)
    } catch (e: Throwable) {
        e.printStackTrace()
        null
    }

    state = DetectedGame(gameWADFile, files, hash, gw)
}

fun ParentNode.render() {
    when (val s = state) {
        is Ready -> {
            el(HtmlTagName.div) {
                className = ClassName("menu")

                el(HtmlTagName.button).apply {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Start Game"
                    }

                    this.clickEvent.addHandler {
                        sessionStorage.setItem("startedFromMainPage", "true")
                        location.href = "/play?id=${s.version.id}"
                    }
                }

                // The native file input can't be styled, so it is hidden inside a label that acts as the button
                el(HtmlTagName.label) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Import Saves"
                    }

                    el(HtmlTagName.input).apply {
                        this.type = InputType.file
                        this.accept = ".zip"

                        this.changeEvent.addHandler {
                            val file = this.files?.item(0) ?: return@addHandler
                            this.disabled = true

                            GlobalScope.launch {
                                OPFSUtils.extractZIPToFolder("versions/${s.version.id}/save/", JSZip().load(file.arrayBuffer()))
                                state = Ready(s.version)
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Export Saves"
                    }

                    this.clickEvent.addHandler {
                        this.disabled = true

                        GlobalScope.launch {
                            try {
                                DownloadUtils.download(OPFSUtils.compressFolderToZIP("versions/${s.version.id}/save/"))
                            } finally {
                                this@el.disabled = false
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Delete Saves"
                    }

                    this.clickEvent.addHandler {
                        val result = confirm("Are you really sure?")

                        if (result) {
                            this.disabled = true

                            GlobalScope.launch {
                                val directory = navigator.storage.getDirectory()
                                try {
                                    directory.getDirectoryHandle("versions", FileSystemGetDirectoryOptions(create = true)).getDirectoryHandle(s.version.id, FileSystemGetDirectoryOptions(create = true)).removeEntry("save", FileSystemRemoveOptions(recursive = true))
                                } catch (e: Throwable) {
                                }
                                state = Ready(s.version)
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Delete Game Data"
                    }

                    this.clickEvent.addHandler {
                        val result = confirm("Are you really sure?")

                        if (result) {
                            this.disabled = true

                            GlobalScope.launch {
                                try {
                                    navigator.storage.getDirectory().getDirectoryHandle("versions", FileSystemGetDirectoryOptions(create = true)).removeEntry(s.version.id, FileSystemRemoveOptions(recursive = true))
                                } catch (e: Throwable) {
                                }

                                updateConfig { it.copy(versions = it.versions - s.version, activeVersion = null) }

                                state = if (config.versions.isNotEmpty()) {
                                    SelectGameVersion
                                } else {
                                    UploadGame
                                }
                            }
                        }
                    }
                }
            }

            el(HtmlTagName.div) {
                className = ClassName("current-version")

                el(HtmlTagName.span) {
                    textContent = s.version.name
                }

                el(HtmlTagName.span) {
                    className = ClassName("undertale-text-button")

                    textContent = "[Change]"
                    this.clickEvent.addHandler {
                        state = SelectGameVersion
                    }
                }
            }
        }

        UploadGame -> {
            el(HtmlTagName.div) {
                className = ClassName("menu")

                // The native file input can't be styled, so it is hidden inside a label that acts as the button
                el(HtmlTagName.label) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Select Game Folder"
                    }

                    el(HtmlTagName.input).apply {
                        this.type = InputType.file
                        this.webkitdirectory = true

                        this.changeEvent.addHandler {
                            GlobalScope.launch {
                                detectGame(files!!)
                            }
                        }
                    }
                }
            }
        }

        is ProcessingGame -> {
            processingGame(s.filename, s.count, s.total)
        }

        is DetectedGame -> {
            val detectedVersion = KNOWN_UNDERTALE_VERSIONS[s.hash]

            el(HtmlTagName.div) {
                el(HtmlTagName.div) {
                    className = ClassName("detected-version")

                    el(HtmlTagName.div) {
                        className = ClassName("detected-version-status")

                        if (s.gameWAD == null) {
                            // The icon must be a sibling of the text, setting textContent on its parent would remove it
                            compatibilityIcon(GameCompatibility.INCOMPATIBLE)

                            el(HtmlTagName.span) {
                                className = ClassName("incompatible")
                                textContent = "Failed to parse Game WAD!"
                            }
                        } else {
                            compatibilityIcon(getGameCompatibility(s.hash))

                            el(HtmlTagName.div) {
                                if (detectedVersion != null) {
                                    className = if (detectedVersion.compatibility == GameCompatibility.COMPATIBLE) ClassName("compatible") else ClassName("incompatible")

                                    el(HtmlTagName.span) {
                                        textContent = detectedVersion.displayName
                                    }

                                    if (detectedVersion.compatibility == GameCompatibility.INCOMPATIBLE) {
                                        el(HtmlTagName.div) {
                                            textContent = "This version is not compatible, only v1.001 to v1.08 are supported!"
                                        }
                                    }
                                } else {
                                    el(HtmlTagName.span) {
                                        className = ClassName("unknown")
                                        textContent = "Unknown game version, it may not work correctly!"
                                    }
                                }
                            }
                        }
                    }

                    // Shown even for known versions, so users can compare it or report an unknown one
                    if (s.gameWAD != null) {
                        el(HtmlTagName.div) {
                            className = ClassName("hash")
                            textContent = "Name: ${getGameDisplayName(s.gameWAD)}"
                        }

                        el(HtmlTagName.div) {
                            className = ClassName("hash")
                            textContent = "WAD Version: ${getWADVersion(s.gameWAD)}"
                        }
                    }

                    el(HtmlTagName.div) {
                        className = ClassName("hash")
                        textContent = "MD5: ${s.hash}"
                    }
                }

                el(HtmlTagName.div) {
                    textContent = "Name"
                }

                val input = el(HtmlTagName.input) {
                    className = ClassName("undertale-text-input")
                    value = detectedVersion?.let { "Undertale ${it.version}" } ?: "Undertale"
                    placeholder = "Undertale"
                }

                val button = el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    textContent = "Import"

                    this.clickEvent.addHandler {
                        disabled = true
                        val gameId = Uuid.random().toString()

                        GlobalScope.launch {
                            OPFSUtils.copyFilesToOPFS(
                                "versions/$gameId/bundle",
                                s.files,
                                { name, count ->
                                    state = ProcessingGame(name, count, s.files.size)
                                }
                            )

                            val version = WebtaleConfig.GameVersion(gameId, input.value, s.gameWADFile.name)
                            updateConfig { it.copy(versions = it.versions + version, activeVersion = gameId) }

                            navigator.storage.persist()

                            state = Ready(version)
                        }
                    }
                }

                fun updateButtonState() {
                    button.disabled = input.value.isBlank() || detectedVersion?.compatibility == GameCompatibility.INCOMPATIBLE || s.gameWAD == null
                }

                updateButtonState()

                input.inputEvent.addHandler {
                    updateButtonState()
                }
            }
        }

        is SelectGameVersion -> {
            el(HtmlTagName.div) {
                className = ClassName("cards-with-header")

                el(HtmlTagName.div) {
                    className = ClassName("card-header")

                    el(HtmlTagName.div) {
                        className = ClassName("card-header-info")

                        el(HtmlTagName.div) {
                            className = ClassName("card-header-title")
                            textContent = "Game Versions"
                        }

                        el(HtmlTagName.div) {
                            className = ClassName("card-header-description")
                            textContent = if (config.versions.size == 1) "1 game version" else "${config.versions.size} game versions"
                        }
                    }

                    el(HtmlTagName.label) {
                        className = ClassName("undertale-button")

                        el(HtmlTagName.div) {
                            textContent = "Add"
                        }

                        el(HtmlTagName.input) {
                            this.type = InputType.file
                            this.webkitdirectory = true

                            this.changeEvent.addHandler {
                                GlobalScope.launch {
                                    detectGame(files!!)
                                }
                            }
                        }
                    }
                }

                for (version in config.versions) {
                    el(HtmlTagName.div) {
                        className = ClassName("game-version")

                        append(version.name)

                        el(HtmlTagName.button) {
                            className = ClassName("undertale-button")

                            textContent = "Select"

                            this.clickEvent.addHandler {
                                GlobalScope.launch {
                                    updateConfig { it.copy(activeVersion = version.id) }
                                    state = Ready(version)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

suspend fun loadConfig() {
    config = try {
        val file = navigator.storage.getDirectory().getFileHandle("config.json")
        Json.decodeFromString<WebtaleConfig>(file.getFile().byteArray().decodeToString())
    } catch (e: Throwable) {
        WebtaleConfig(listOf(), null)
    }
}

suspend fun updateConfig(block: (WebtaleConfig) -> WebtaleConfig) {
    config = block(config)
    val writable = navigator.storage.getDirectory()
        .getFileHandle("config.json", FileSystemGetFileOptions(create = true))
        .createWritable()
    writable.write(Json.encodeToString(config).encodeToByteArray())
    writable.close()
}

fun main() {
    GlobalScope.launch {
        loadConfig()

        val path = document.location.pathname
        val params = URLSearchParams(window.location.search)

        if (path == "/play") {
            val gameVersion = config.versions.firstOrNull { it.id == params.get("id") }

            if (gameVersion == null || sessionStorage.getItem("startedFromMainPage") != "true") {
                location.href = "/"
                return@launch
            }

            // We won't add to the body yet
            val gameCanvas = document.createElement(HtmlTagName.canvas).apply {
                width = 640
                height = 480
            }

            createRunner("versions/${gameVersion.id}/bundle", "versions/${gameVersion.id}/save", gameVersion.gameWADName, gameCanvas).await()

            val gameContainer = document.getElementById(ElementId("game-container"))!!
            gameContainer.replaceChildren()

            gameContainer.clickToStart(gameContainer, gameCanvas) {
                window.addEventHandler(KeyboardEvent.KEY_DOWN) { event ->
                    // Useful when the user has not clicked on the page yet
                    // audioManager.resumeAudioContext()
                    if (event.repeat)
                        return@addEventHandler

                    event.preventDefault()

                    onKeyDown(convertWebKeyCodeToGMLVK(event.code.toString()))
                }

                window.addEventHandler(KeyboardEvent.KEY_UP) { event ->
                    if (event.repeat)
                        return@addEventHandler

                    event.preventDefault()

                    onKeyUp(convertWebKeyCodeToGMLVK(event.code.toString()))
                }

                VirtualGamepad.attachIfTouchDevice(gameContainer, WebtaleInputSink)

                startRunner {
                    location.href = "/"
                }
            }
        } else {
            if (config.versions.isEmpty()) {
                state = UploadGame
            } else {
                state = Ready(config.versions.firstOrNull { it.id == config.activeVersion } ?: config.versions.first())
            }
        }
    }
}

fun convertWebKeyCodeToGMLVK(code: String) = when {
    code.length == 4 && code.startsWith("Key") -> code[3].code // "KeyA" -> VK_A (65)
    code.length == 6 && code.startsWith("Digit") -> code[5].code // "Digit0" -> 48
    code.length == 7 && code.startsWith("Numpad") && code[6].isDigit() -> RunnerKeyboard.VK_NUMPAD0 + (code[6] - '0')
    code.startsWith("F") && code.drop(1).toIntOrNull() in 1..12 -> RunnerKeyboard.VK_F1 + (code.drop(1).toInt() - 1)
    else -> when (code) {
        "Backspace" -> RunnerKeyboard.VK_BACKSPACE
        "Tab" -> RunnerKeyboard.VK_TAB
        "Enter" -> RunnerKeyboard.VK_ENTER
        "Escape" -> RunnerKeyboard.VK_ESCAPE
        "Space" -> RunnerKeyboard.VK_SPACE
        "ArrowLeft" -> RunnerKeyboard.VK_LEFT
        "ArrowRight" -> RunnerKeyboard.VK_RIGHT
        "ArrowUp" -> RunnerKeyboard.VK_UP
        "ArrowDown" -> RunnerKeyboard.VK_DOWN
        "ShiftLeft" -> RunnerKeyboard.VK_LSHIFT
        "ShiftRight" -> RunnerKeyboard.VK_RSHIFT
        "ControlLeft" -> RunnerKeyboard.VK_LCONTROL
        "ControlRight" -> RunnerKeyboard.VK_RCONTROL
        "AltLeft" -> RunnerKeyboard.VK_LALT
        "AltRight" -> RunnerKeyboard.VK_RALT
        "Home" -> RunnerKeyboard.VK_HOME
        "End" -> RunnerKeyboard.VK_END
        "Insert" -> RunnerKeyboard.VK_INSERT
        "Delete" -> RunnerKeyboard.VK_DELETE
        "PageUp" -> RunnerKeyboard.VK_PAGEUP
        "PageDown" -> RunnerKeyboard.VK_PAGEDOWN
        "Pause" -> RunnerKeyboard.VK_PAUSE
        "PrintScreen" -> RunnerKeyboard.VK_PRINTSCREEN
        "NumpadMultiply" -> RunnerKeyboard.VK_MULTIPLY
        "NumpadAdd" -> RunnerKeyboard.VK_ADD
        "NumpadSubtract" -> RunnerKeyboard.VK_SUBTRACT
        "NumpadDecimal" -> RunnerKeyboard.VK_DECIMAL
        "NumpadDivide" -> RunnerKeyboard.VK_DIVIDE
        "NumpadEnter" -> RunnerKeyboard.VK_ENTER
        else -> -1
    }
}
object WebtaleInputSink : VirtualInputSink {
    override fun keyDown(vk: Int) = onKeyDown(vk)
    override fun keyUp(vk: Int) = onKeyUp(vk)
}
